import React from 'react'
import { useSelector } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import { Form, Formik } from 'formik'
import * as Yup from 'yup'
import { Typography, ContactInfoFormBody, LanguageSection } from 'components'
import { CONSTANTS, colors } from '../../constants/index'

const useStyles = makeStyles({
  stepButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 40,
  },
  title: {
    marginBottom: 8,
  },
  subtitle: {
    marginBottom: 24,
  },
  languageSection: {
    marginTop: 40,
  },
})

const ContactInfoForm = props => {
  const {
    contactInfo,
    countries,
    states,
    provinces,
    onSubmit,
    onReset,
    footer,
    saveCurrentUserLanguages,
    updateLocalUserLanguages,
    languages,
    languagesDB,
    className,
    updateContactInfo = () => {},
  } = props

  const classes = useStyles()
  const currentUser = useSelector(state => state.auth.currentUser)
  const isAccountant = currentUser?.subrole === 'accountant'

  const initialValues = {
    street: contactInfo.street,
    suite: contactInfo.suite,
    city: contactInfo.city,
    state: contactInfo.state,
    zip: contactInfo.zip,
    phone: contactInfo.phone,
    optionalPhone: contactInfo.optionalPhone,
    country: contactInfo.country,
    ...(isAccountant && { firmName: contactInfo?.firm_name || '' }),
  }

  // Dynamic validation schema based on role
  const getValidationSchema = () => {
    const baseSchema = {
      street: Yup.string()
        .trim()
        .max(100, 'Please ensure this field has no more than 100 characters')
        .required('Please fill in the field'),
      suite: Yup.string()
        .trim()
        .max(50, 'Please ensure this field has no more than 50 characters'),
      city: Yup.string()
        .trim()
        .max(50, 'Please ensure this field has no more than 50 characters')
        .required('Please fill in the field'),
      state: Yup.string()
        .trim()
        .required('Please fill in the field'),
      phone: Yup.string()
        .test('phone-validation', 'Please enter a valid phone number', function(value) {
          if (!value) return false

          const country = this.parent.country
          const phoneValue = value.toString().replace(/([+ -])/g, '')

          if (country === 124) {
            // Canada
            // Canadian phone: +1 + 10 digits = 11 total
            return phoneValue.length === 11 && phoneValue.startsWith('1')
          } else {
            // USA (default)
            // US phone: +1 + 10 digits = 11 total
            return phoneValue.length === 11 && phoneValue.startsWith('1')
          }
        })
        .required('Please fill in the field'),
      optionalPhone: Yup.string().test('phone-validation', 'Please enter a valid phone number', function(value) {
        if (!value || value === '+1') return true

        const country = this.parent.country
        const phoneValue = value.toString().replace(/([+ -])/g, '')

        if (country === 124) {
          return phoneValue.length === 11 && phoneValue.startsWith('1')
        } else {
          return phoneValue.length === 11 && phoneValue.startsWith('1')
        }
      }),
    }

    if (isAccountant) {
      baseSchema.firmName = Yup.string()
        .trim()
        .max(50, 'Please ensure this field has no more than 50 characters')
        .required('Please fill in the field')
    }

    if (isAccountant) {
      baseSchema.country = Yup.string()
        .trim()
        .required('Please fill in the field')
    }

    baseSchema.zip = Yup.string()
      .trim()
      .required('Please fill in the field')
      .when('country', (country, schema) => {
        if (country == 124) {
          return schema.matches(/^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/, 'Please enter a valid postal code')
        }
        return schema.matches(/^\d{5}$/, 'Only digits available')
      })

    return Yup.object(baseSchema)
  }

  return (
    <div>
      <Typography classes={{ h4: classes.title }} variant="h4">
        Contact Info
      </Typography>
      <Typography variant="subtitle1" className={classes.subtitle}>
        Only your city and state will be displayed publicly
      </Typography>

      <Formik
        initialValues={initialValues}
        validationSchema={getValidationSchema()}
        onSubmit={onSubmit}
        onReset={onReset}
        enableReinitialize
      >
        {props => (
          <Form>
            <ContactInfoFormBody
              countries={countries}
              states={states}
              provinces={provinces}
              updateContactInfo={updateContactInfo}
              disableCountryField={true}
            />
            {languagesDB && (
              <LanguageSection
                languages={languages}
                containerClass={classes.languageSection}
                languagesDB={languagesDB}
                updateLocalUserLanguages={updateLocalUserLanguages}
              />
            )}
            {footer && (
              <div className={classes.stepButtons}>{React.cloneElement(footer, { disabled: !props.dirty })}</div>
            )}
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default ContactInfoForm
